import * as React from 'react'
import { FC, useMemo } from 'react'
import type { MenuProps as AntdMenuProps } from 'antd'
import { Menu as AntdMenu } from 'antd'
import { Link, useLocation } from 'react-router-dom'
import { routes } from '../../constants/routes'
import { useAppSelector } from '../../store/store'
import { isRouteAccessible } from '../../constants/route-permissions'

type MenuItem = Required<AntdMenuProps>['items'][number]

const menuItems: MenuItem[] = [
    {
        label: <Link to={routes.home}>Дашборд</Link>,
        key: routes.home,
    },
    {
        label: 'Оборудование',
        key: 'equipment',
        children: [
            {
                // label: <Link to={routes.equipmentStamps}>Штампы</Link>,
                label: 'Штампы',
                key: 'equipment__stamps',
                children: [
                    {
                        label: (
                            <Link to={routes.equipmentStampsLabelPrinting}>
                                Печать метки тубуса
                            </Link>
                        ),
                        key: 'stamp-label-printing',
                    },
                ],
            },
            {
                label: <Link to={routes.equipmentFlexoForms}>Флексоформы</Link>,
                key: routes.equipmentFlexoForms,
            },
            {
                label: <Link to={routes.equipmentRollers}>Валы</Link>,
                key: 'equipment__rollers',
            },
        ],
    },
    {
        label: 'Приход',
        key: 'arrivals',
        children: [
            {
                label: <Link to={routes.receiptsMaterials}>Материалы</Link>,
                key: 'arrivals__material',
            },
            {
                label: <Link to={routes.receiptsFlexoForms}>Флексоформы</Link>,
                key: routes.receiptsFlexoForms,
            },
            {
                label: 'Другое',
                key: 'arrivals__other',
            },
        ],
    },
    {
        label: <Link to={routes.consumption}>Расход</Link>,
        key: routes.consumption,
    },
    {
        label: 'Остатки',
        key: 'balances',
        children: [
            {
                label: <Link to={routes.balancesMaterials}>Материалы</Link>,
                key: 'balances__material',
            },
            {
                label: 'Другое',
                key: 'balances__other',
            },
        ],
    },
    {
        key: 'purchases',
        label: 'Закупки',
        children: [
            {
                type: 'group',
                label: 'Оборудование',
                children: [
                    {
                        label: <Link to={routes.purchasesStampOrders}>Штампы</Link>,
                        key: 'purchases__stamps',
                    },
                    {
                        label: 'Флексоформы',
                        key: 'purchases__flex-forms',
                    },
                ],
            },
            {
                label: <Link to={routes.purchasesMaterialOrders}>Материалы</Link>,
                key: 'purchases__materials',
            },
            {
                label: <Link to={routes.purchasesMaterialOrderRequests}>Заказ материала</Link>,
                key: 'purchases__material-order-requests',
            },
        ],
    },
    {
        key: 'writeOffs',
        label: 'Списания',
        children: [
            {
                type: 'group',
                label: 'Оборудование',
                children: [
                    {
                        label: <Link to={routes.writeOffsStampWriteOffs}>Штампы</Link>,
                        key: 'writeOffs__stamps',
                    },
                    {
                        label: 'Флексоформы',
                        key: 'writeOffs__flex-forms',
                    },
                ],
            },
            {
                label: 'Материалы',
                key: 'writeOffs__materials',
            },
        ],
    },
    {
        label: <Link to={routes.stampMainPage}>Штампы</Link>,
        key: routes.stampMainPage,
    },
    {
        label: <Link to={routes.calculations}>Расчёты</Link>,
        key: routes.calculations,
    },
    {
        label: <Link to={routes.shipments}>Отгрузки</Link>,
        key: routes.shipments,
    },
    {
        label: <Link to={routes.ribbons}>Риббоны</Link>,
        key: routes.ribbons,
    },
    {
        label: <Link to={routes.orders}>Заказы</Link>,
        key: routes.orders,
    },
    {
        label: <Link to={routes.shifts}>Смены</Link>,
        key: routes.shifts,
    },
    {
        key: 'schedules',
        label: 'Планирование',
        children: [
            {
                label: <Link to={routes.schedulesFlexo}>Печать</Link>,
                key: 'schedules__flexo',
            },
            {
                label: <Link to={routes.schedulesRewind}>Перемотка</Link>,
                key: 'schedules__rewind',
            },
        ],
    },
    {
        label: <Link to={routes.schedulesFlexoMachines}>Печать</Link>,
        key: 'schedules__flexo__machines',
    },
    {
        label: <Link to={routes.schedulesRewindMachines}>Перемотка</Link>,
        key: 'schedules__rewind__machines',
    },

    { key: 'print-history', label: <Link to={routes.printHistory}>История печати</Link> },
    {
        key: routes.volumeSummary,
        label: <Link to={routes.volumeSummary}>Сводка по объему</Link>,
    },
    { 
        label: <Link to={routes.rewindHistory}>История перемотки</Link>,
        key: routes.rewindHistory, 
    },
    {
        label: 'Задачи дизайнера',
        key: 'designer-tasks',
        children: [
            {
                label: <Link to={routes.designerTasks}>Задачи</Link>,
                key: routes.designerTasks,
            },
            {
                label: <Link to={routes.designerTasksOvertime}>Переработки</Link>,
                key: routes.designerTasksOvertime,
            },
        ],
    },
    {
        label: <Link to={routes.curatorTasks}>Задачи куратора</Link>,
        key: routes.curatorTasks,
    },

    {
        label: <Link to={routes.clients}>Клиенты</Link>,
        key: 'clients',
    },
    {
        label: <Link to={routes.printPreparation}>Подготовка к печати</Link>,
        key: 'print_preparation',
    },
    {
        label: <Link to={routes.technicalCards}>Технические карты</Link>,
        key: 'technical_cards',
    },
]

/**
 * Рекурсивно фильтрует пункты меню на основе доступа к роутам
 */
const filterMenuItems = (items: MenuItem[], userRoles: string[] = []): MenuItem[] => {
    return items
        .map(item => {
            if (!item) {
                return null
            }

            // Если у пункта есть children, рекурсивно фильтруем их
            if ('children' in item && item.children) {
                const filteredChildren = filterMenuItems(item.children, userRoles)

                // Если после фильтрации не осталось дочерних элементов, скрываем родительский пункт
                if (filteredChildren.length === 0) {
                    return null
                }

                return {
                    ...item,
                    children: filteredChildren,
                } as MenuItem
            }

            // Проверяем доступ к роуту, если это ссылка
            if ('label' in item && item.label) {
                const linkElement = React.isValidElement(item.label) ? item.label.props?.to : null

                if (linkElement) {
                    // Преобразуем роли пользователя в BusinessRole enum
                    const businessRoles = userRoles as any[]
                    const hasAccess = isRouteAccessible(linkElement, businessRoles)

                    if (!hasAccess) {
                        return null
                    }
                } else {
                    // Если пункт меню не имеет ссылки и не имеет children,
                    // скрываем его (белый список - показываем только то, что явно разрешено)
                    // Но если есть children, они уже обработаны выше
                    return null
                }
            }

            return item
        })
        .filter((item): item is MenuItem => item !== null)
}

interface MenuProps { }

const Menu: FC<MenuProps> = () => {
    const location = useLocation()
    const user = useAppSelector(state => state.auth.user)

    // Фильтруем пункты меню на основе ролей пользователя
    const filteredItems = useMemo(() => {
        const userRoles = user?.businessRoles || []
        return filterMenuItems(menuItems, userRoles)
    }, [user?.businessRoles])

    return <AntdMenu selectedKeys={[location.pathname]} mode='horizontal' items={filteredItems} />
}

export default Menu
